import { useMemo } from 'react';
import { useSelector } from 'react-redux';
import { AgGridReact } from 'ag-grid-react';
import 'ag-grid-community/styles/ag-grid.css';
import 'ag-grid-community/styles/ag-theme-material.css';
import './ag-theme-base.css';
import '@ag-grid-enterprise/row-grouping';
import { ModuleRegistry, provideGlobalGridOptions } from 'ag-grid-community';
import { AllEnterpriseModule, LicenseManager } from 'ag-grid-enterprise';
import './style.css';
import TCAPLoadingOverlay from './TCAPLoadingOverlay';

LicenseManager.setLicenseKey(import.meta.env.VITE_AG_GRID_LICENSE_KEY);
ModuleRegistry.registerModules([AllEnterpriseModule]);
provideGlobalGridOptions({ theme: 'legacy' });

// AGGridBase is to be used instead of redefining AgGridReact as we only want to define it once due to the set up with the license
const AGGridBase = ({
    gridRef,
    tableData,
    masterDetail,
    isLoading,
    quickFilterText,
    customCellRenderer,
    defaultColDef,
    autoGroupColumnDef,
    autoSizeStrategy,
    isRowMaster,
    rowModelType,
    onStateUpdated,
    onColumnMoved,
    defaultCsvExportParams,
    defaultExcelExportParams,
    rowSelection,
    onRowClicked,
    groupingHeader,
    onSelectionChanged,
    overlayNoRowsTemplate,
    pagination = true,
    paginationPageSize = 50,
    paginationSizeSelector: paginationPageSizeSelector = [
        10, 50, 100, 250, 500,
    ],
    useFixedHeight = true,
    customHeight = '',
    initialState, // Add initialState prop for AG Grid state management
    ...props
}) => {
    const darkMode = useSelector((state) => state.settings.darkMode);

    const loadingOverlayComponentParams = useMemo(() => {
        return {
            darkMode,
            loadingMessage: 'Loading',
        };
    }, [darkMode]);

    const mergedDefaultColDef = useMemo(
        () => ({
            ...defaultColDef,
            cellRenderer: customCellRenderer,
        }),
        [defaultColDef, customCellRenderer],
    );

    const mergedDetailCellRendererParams = useMemo(() => {
        if (!tableData.detailCellRendererParams) return undefined;

        return {
            ...tableData.detailCellRendererParams,
            detailGridOptions: {
                ...tableData.detailCellRendererParams.detailGridOptions,
                popupParent: document.body,
            },
        };
    }, [tableData.detailCellRendererParams]);

    return (
        <div
            className={
                darkMode
                    ? 'ag-theme-material-dark ag-theme-base'
                    : 'ag-theme-material ag-theme-base ag-theme-base-light'
            }
            style={{
                height: useFixedHeight
                    ? window.innerHeight - window.innerHeight / 3.8
                    : customHeight,
                width: '100%',
            }}>
            <AgGridReact
                loadThemeGoogleFonts
                ref={gridRef}
                columnDefs={tableData.columns}
                rowData={tableData.data}
                loading={isLoading}
                loadingOverlayComponent={TCAPLoadingOverlay}
                loadingOverlayComponentParams={loadingOverlayComponentParams}
                suppressServerSideFullWidthLoadingRow={true}
                autoGroupColumnDef={autoGroupColumnDef}
                pagination={pagination}
                paginationPageSize={paginationPageSize}
                paginationPageSizeSelector={paginationPageSizeSelector}
                animateRows={true}
                masterDetail={masterDetail}
                isRowMaster={isRowMaster}
                quickFilterText={quickFilterText}
                includeHiddenColumnsInQuickFilter={true}
                groupDisplayType="groupRows"
                rowGroupPanelShow={groupingHeader}
                detailRowAutoHeight={true}
                rowModelType={rowModelType}
                rowSelection={rowSelection}
                onSelectionChanged={onSelectionChanged}
                onRowClicked={onRowClicked}
                suppressDragLeaveHidesColumns={true}
                headerHeight={42}
                rowHeight={42}
                defaultColDef={mergedDefaultColDef}
                defaultCsvExportParams={defaultCsvExportParams}
                defaultExcelExportParams={defaultExcelExportParams}
                detailCellRendererParams={mergedDetailCellRendererParams}
                autoSizeStrategy={autoSizeStrategy}
                onStateUpdated={onStateUpdated}
                onColumnMoved={onColumnMoved}
                overlayNoRowsTemplate={overlayNoRowsTemplate}
                popupParent={document.body}
                {...props}
                initialState={initialState}
            />
        </div>
    );
};

export default AGGridBase;
